import cx from 'classnames'
import { Popover } from 'components/common/Popover'
import { Field } from 'formik'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'
import { Input } from './Input'

export type CheckboxField =
  | string
  | {
      name: string | React.ReactNode
      value: string
      tooltip?: string
      noColorStyle?: boolean
    }

type Props = {
  name: string
  fields: CheckboxField[]
  showAll?: boolean
  hasSearch?: boolean
  searchPlaceholder?: string
  hasColors?: boolean
  className?: string
  onSearch?: (value: string) => void
  errors?: Record<string, string | undefined>
}

export const CheckboxGroup = ({
  name,
  fields = [],
  showAll = false,
  hasSearch = false,
  searchPlaceholder = 'Search',
  hasColors = false,
  className,
  onSearch,
  errors,
}: Props) => {
  const { t } = useTranslation()
  const [internalFields, setInternalFields] = useState<CheckboxField[]>(fields)
  const [showMore, setShowMore] = useState(true)
  const [filter, setFilter] = useState('')

  useEffect(() => {
    if (onSearch) {
      setInternalFields(fields)
      return
    }

    if (filter !== '') {
      setInternalFields(
        fields.filter(field => {
          if (typeof field === 'string') {
            return field.toLowerCase().includes(filter.toLowerCase())
          }
          if (field.name && typeof field.name === 'string') {
            return field.name.toLowerCase().includes(filter.toLowerCase())
          }
        })
      )
    } else {
      setInternalFields(fields)
    }
  }, [filter, fields, setInternalFields, onSearch])

  const getColorStyle = (field: CheckboxField) => {
    if (typeof field === 'string' || field.noColorStyle) return

    const isMulti = field.value.toLowerCase() === 'multi'
    return {
      background: isMulti ? 'url(/files/multi.png)' : field.value,
      backgroundSize: isMulti ? 'cover' : undefined,
      borderWidth: field.value.toLowerCase() === '#ffffff' ? '1px' : undefined,
      borderColor:
        field.value.toLowerCase() === '#ffffff'
          ? 'rgb(217 219 233)'
          : undefined,
    }
  }

  const getValue = (field: CheckboxField) => {
    if (typeof field === 'string') return field
    if (hasColors) return field.name
    return field.value
  }

  return (
    <div className={`mt-4 flex w-full flex-col space-y-3 ${className}`}>
      {hasSearch && (
        <Input
          type='search'
          className='mb-2'
          placeholder={searchPlaceholder}
          onChange={event => {
            if (onSearch) {
              onSearch(event.target.value)
              return
            }

            setFilter(event.target.value)
          }}
        />
      )}

      <div
        className={cx('flex flex-col space-y-3', {
          'items-start': !hasColors || typeof fields[0] === 'string',
        })}
      >
        {internalFields
          .slice(0, !showAll && showMore ? 6 : internalFields.length)
          .map((field, index) => {
            const fieldValue = typeof field === 'string' ? field : field.value
            const colorStyle = getColorStyle(field)

            return (
              <div key={index} className='flex flex-col'>
                <div className='flex items-center'>
                  <label className='inline-flex w-full cursor-pointer items-center text-sm'>
                    <Field
                      name={name}
                      type='checkbox'
                      value={getValue(field)}
                      className='mr-3'
                      inputClassName={cx('', {
                        '!border-danger-dark': Boolean(errors?.[fieldValue]),
                      })}
                      as={Input}
                    />

                    {typeof field === 'string' && field}
                    {typeof field !== 'string' && (
                      <>
                        <span className='flex w-full justify-between'>
                          {field.name}
                          {colorStyle && (
                            <span
                              style={colorStyle}
                              className='h-6 w-6 rounded-full'
                            ></span>
                          )}
                        </span>
                      </>
                    )}
                  </label>

                  {typeof field !== 'string' && field.tooltip && (
                    <Popover
                      content={field.tooltip}
                      triggerClassName='ml-1.5'
                      portalled={false}
                      side='top'
                    />
                  )}
                </div>

                {errors?.[fieldValue] && (
                  <span className='ml-8 text-sm text-danger-dark'>
                    {errors[fieldValue]}
                  </span>
                )}
              </div>
            )
          })}
      </div>

      {!showAll && internalFields.length > 6 && (
        <span
          className='cursor-pointer text-sm text-secondary-default'
          onClick={() => setShowMore(!showMore)}
        >
          {showMore ? t('common:show_more') : t('common:show_less')}
        </span>
      )}
    </div>
  )
}
